import React, { useState, useRef, useEffect, useCallback, useContext } from "react";
import {
  Bell, Trash2, CheckCheck, Package, Tag, Settings, ChevronDown, Filter,
} from "lucide-react";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";
import "../../../../assets/css/customer/customerPannel/notifications/Notifications.css";
import axios from "axios";
import AppContext from "../../../../context/AppContext";

// ── Type meta ──────────────────────────────────────────────────────────────────
const TYPE_META = {
  order:     { label: "Order",  color: "#5C6B47", bg: "rgba(92,107,71,0.12)",   Icon: Package  },
  promotion: { label: "Promo",  color: "#D67E43", bg: "rgba(214,126,67,0.12)",  Icon: Tag      },
  system:    { label: "System", color: "#6B4C3B", bg: "rgba(107,76,59,0.12)",   Icon: Settings },
};

const getTypeMeta = (type) =>
  TYPE_META[type] || { label: type, color: "#888", bg: "rgba(136,136,136,0.1)", Icon: Bell };

// ── Component ──────────────────────────────────────────────────────────────────
const Notifications = () => {
  const { baseUrl, authToken, fetchNotificationCount } = useContext(AppContext);

  const [notifications, setNotifications] = useState([]);
  const [filter, setFilter]               = useState("all");
  const [dropdownOpen, setDropdownOpen]   = useState(false);
  const [menuPos, setMenuPos]             = useState({ top: 0, left: 0 });
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState(null);

  const triggerRef = useRef(null);
  const menuRef    = useRef(null);

  const headers = { Authorization: `Bearer ${authToken}` };

  // ── Fetch notifications ──────────────────────────────────────────────────────
  const fetchNotifications = useCallback(() => {
  setLoading(true);

  axios
    .get(`${baseUrl}/customer/notification/getNotification`, { headers })
    .then((res) => {

      console.log("Full Response:", res.data);
      console.log("Notifications:", res.data.notifications);

      setNotifications(res.data.notifications || []);
      setError(null);
    })
    .catch((err) => {
      console.log("Notification Error:", err.response?.data);

      setError("Failed to load notifications");
      setNotifications([]);
    })
    .finally(() => setLoading(false));

}, [baseUrl, authToken]);

  useEffect(() => { fetchNotifications(); }, [fetchNotifications]);

  // ── Derived values ───────────────────────────────────────────────────────────
  const unreadCount = notifications.filter((n) => n.status === "unread").length;

  // ── Mark single as read ──────────────────────────────────────────────────────
  const markAsRead = (id) => {
    // if already read, no API call needed
    const notif = notifications.find((n) => n.id === id);
    if (!notif || notif.status === "read") return;

    axios
      .post(`${baseUrl}/customer/notification/read/${id}`, {}, { headers })
      .then(() => {
        setNotifications((prev) =>
          prev.map((n) => (n.id === id ? { ...n, status: "read" } : n))
        );
        fetchNotificationCount();
      })
      .catch(() => Swal.fire("Error", "Could not mark as read", "error"));
  };

  // ── Mark ALL as read — single API call ──────────────────────────────────────
  const markAllAsRead = () => {
    if (unreadCount === 0) return;

    axios
      .post(`${baseUrl}/customer/notification/read-all`, {}, { headers })
      .then(() => {
        setNotifications((prev) => prev.map((n) => ({ ...n, status: "read" })));
        fetchNotificationCount();
        Swal.fire({
          icon: "success", title: "Done!", text: "All notifications marked as read",
          timer: 1500, showConfirmButton: false,
        });
      })
      .catch(() => Swal.fire("Error", "Could not mark all as read", "error"));
  };

  // ── Delete single notification ───────────────────────────────────────────────
  const deleteNotification = (id) => {
    Swal.fire({
      title: "Delete Notification?",
      text: "This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6B4C3B",
      confirmButtonText: "Delete",
    }).then((result) => {
      if (!result.isConfirmed) return;

      axios
        .delete(`${baseUrl}/customer/notification/delete/${id}`, { headers })
        .then(() => {
          setNotifications((prev) => prev.filter((n) => n.id !== id));
        })
        .catch(() => Swal.fire("Error", "Could not delete notification", "error"));
    });
  };

  // ── Clear ALL notifications ──────────────────────────────────────────────────
  const clearAll = () => {
    if (notifications.length === 0) return;

    Swal.fire({
      title: "Clear All Notifications?",
      text: "All notifications will be deleted permanently.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6B4C3B",
      confirmButtonText: "Clear All",
    }).then((result) => {
      if (!result.isConfirmed) return;

      axios
        .delete(`${baseUrl}/customer/notification/clear-all`, { headers })
        .then(() => {
          setNotifications([]);
          Swal.fire({
            icon: "success", title: "Cleared!", text: "All notifications deleted",
            timer: 1500, showConfirmButton: false,
          });
        })
        .catch(() => Swal.fire("Error", "Could not clear notifications", "error"));
    });
  };

  // ── Filter config ────────────────────────────────────────────────────────────
  const filters = [
    { key: "all",       label: "All",        count: notifications.length },
    { key: "unread",    label: "Unread",      count: unreadCount },
    { key: "order",     label: "Orders",      count: notifications.filter((n) => n.type === "order").length },
    { key: "promotion", label: "Promotions",  count: notifications.filter((n) => n.type === "promotion").length },
    { key: "system",    label: "System",      count: notifications.filter((n) => n.type === "system").length },
  ];

  const activeFilter = filters.find((f) => f.key === filter);

  const filtered = notifications.filter((n) => {
    if (filter === "all")    return true;
    if (filter === "unread") return n.status === "unread";
    return n.type === filter;
  });

  // ── Dropdown position ────────────────────────────────────────────────────────
  const openDropdown = () => {
    if (triggerRef.current) {
      const rect       = triggerRef.current.getBoundingClientRect();
      const menuWidth  = 200;
      const vw         = window.innerWidth;
      let left = rect.right - menuWidth;
      if (left < 8) left = rect.left;
      if (left + menuWidth > vw - 8) left = vw - menuWidth - 8;
      setMenuPos({ top: rect.bottom + 8, left });
    }
    setDropdownOpen((p) => !p);
  };

  useEffect(() => {
    const handler = (e) => {
      if (
        menuRef.current && !menuRef.current.contains(e.target) &&
        triggerRef.current && !triggerRef.current.contains(e.target)
      ) setDropdownOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    const close = () => setDropdownOpen(false);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => { window.removeEventListener("scroll", close, true); window.removeEventListener("resize", close); };
  }, []);

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="ntf-page">

      {/* HEADER */}
      <div className="ntf-header">
        <div className="ntf-header-left">
          <div className="ntf-header-icon">
            <Bell size={20} strokeWidth={1.8} />
            {unreadCount > 0 && <span className="ntf-badge-dot">{unreadCount}</span>}
          </div>
          <div>
            <h1 className="ntf-title">Notifications</h1>
            <p className="ntf-subtitle">
              {unreadCount > 0 ? `${unreadCount} unread` : "All caught up!"}
            </p>
          </div>
        </div>

        <div className="ntf-header-right">
          {/* Filter dropdown trigger */}
          <button
            ref={triggerRef}
            className={`ntf-dd-btn ${dropdownOpen ? "ntf-dd-btn-open" : ""}`}
            onClick={openDropdown}
          >
            <Filter size={13} strokeWidth={2.5} />
            <span>{activeFilter?.label}</span>
            {activeFilter?.count > 0 && <span className="ntf-dd-chip">{activeFilter.count}</span>}
            <ChevronDown size={13} strokeWidth={2.5} className="ntf-dd-chevron" />
          </button>

          {/* Mark all read */}
          <button
            className="ntf-ctrl-btn"
            onClick={markAllAsRead}
            title="Mark all as read"
            disabled={unreadCount === 0}
          >
            <CheckCheck size={15} strokeWidth={2} />
            <span className="ntf-ctrl-label">Read all</span>
          </button>

          {/* Clear all */}
          <button
            className="ntf-ctrl-btn ntf-ctrl-danger"
            onClick={clearAll}
            title="Clear all"
            disabled={notifications.length === 0}
          >
            <Trash2 size={15} strokeWidth={2} />
            <span className="ntf-ctrl-label">Clear</span>
          </button>
        </div>
      </div>

      {/* DROPDOWN MENU */}
      {dropdownOpen && (
        <>
          <div className="ntf-backdrop" onClick={() => setDropdownOpen(false)} />
          <div ref={menuRef} className="ntf-dd-menu" style={{ top: menuPos.top, left: menuPos.left }}>
            <p className="ntf-dd-menu-title">Filter by</p>
            {filters.map((f) => (
              <button
                key={f.key}
                className={`ntf-dd-item ${filter === f.key ? "ntf-dd-item-on" : ""}`}
                onClick={() => { setFilter(f.key); setDropdownOpen(false); }}
              >
                <span>{f.label}</span>
                <span className="ntf-dd-item-count">{f.count}</span>
              </button>
            ))}
          </div>
        </>
      )}

      {/* CONTENT */}
      {loading ? (
        <div className="ntf-loading">
          <Bell size={44} strokeWidth={1.2} color="#D67E43" style={{ opacity: 0.4 }} />
          <p>Loading notifications...</p>
        </div>
      ) : error ? (
        <div className="ntf-error">
          <Bell size={44} strokeWidth={1.2} color="#d33" style={{ opacity: 0.4 }} />
          <h3>Oops!</h3>
          <p>{error}</p>
          <button onClick={fetchNotifications} className="ntf-retry-btn">Retry</button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="ntf-empty">
          <Bell size={44} strokeWidth={1.2} color="#D67E43" style={{ opacity: 0.4 }} />
          <h3>No Notifications</h3>
          <p>You're all caught up!</p>
        </div>
      ) : (
        <div className="ntf-list">
          {filtered.map((n) => {
            const meta     = getTypeMeta(n.type);
            const TypeIcon = meta.Icon;
            return (
              <div
                key={n.id}
                className={`ntf-item ${n.status === "read" ? "ntf-item-read" : "ntf-item-unread"}`}
                onClick={() => markAsRead(n.id)}
              >
                <div className="ntf-item-icon" style={{ background: meta.bg, color: meta.color }}>
                  <TypeIcon size={18} strokeWidth={1.8} />
                </div>

                <div className="ntf-item-body">
                  <div className="ntf-item-top">
                    <span className="ntf-item-title">{n.title}</span>
                    <span className="ntf-type-badge" style={{ color: meta.color, background: meta.bg }}>
                      {meta.label}
                    </span>
                  </div>
                  <p className="ntf-item-msg">{n.message || n.description}</p>
                  <span className="ntf-item-time">
                    {new Date(n.created_at).toLocaleDateString()}
                  </span>
                </div>

                <div className="ntf-item-actions">
                  {n.status === "unread" && <span className="ntf-unread-dot" />}
                  <button
                    className="ntf-delete-btn"
                    onClick={(e) => { e.stopPropagation(); deleteNotification(n.id); }}
                    title="Delete"
                  >
                    <Trash2 size={14} strokeWidth={2} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Notifications;